import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Quote } from 'lucide-react';

const testimonials = [
  {
    id: '1',
    quote: "Damian Depasquale Creative Studio delivered an exceptional design for Nautical Marine Malta Ltd’s showcase for the Malta Boat Show. His creativity, attention to detail, and professional approach truly elevated our brand. Highly recommended for anyone seeking quality graphic design work.",
    author: 'Giovann Xerri',
    role: 'Founder',
    company: 'Nautical Marine Malta Ltd.',
    category: 'Branding',
  },
  {
    id: '2',
    quote: "Damian Depasquale Creative Studio exceeded all expectations from start to finish. Their creativity, attention to detail, and professionalism truly stand out. They listened carefully to our needs, offered smart solutions, and delivered high-quality work within the agreed timeframe. Communication was clear and efficient throughout the whole process, making everything smooth and stress-free. Highly recommended to anyone looking for reliable, modern, and top-level design services. We will definitely be working with Damian Depasquale Creative Studio again.",
    author: 'David Apap Agius',
    role: 'Mayor',
    company: 'Għarb Local Council',
    category: 'Social Media',
  },
  {
    id: '3',
    quote: "I had the pleasure of working with Damian Depasquale Creative Studio for the logo of a local feast organisation I make part of and I couldn’t be happier with the result. From the very beginning, Damian Depasquale Creative Studio was professional, creative, and very easy to communicate with, almost communicating with me everyday. He took the time to understand exactly what I wanted, the identity of the association, and the traditional yet modern feel we were looking for. The attention to detail and creativity that went into the logo was impressive. DDesign provided us with great ideas and was very patient with revisions, making sure the final design was exactly how we envisioned it. The final logo is clean, meaningful, and represents our association perfectly. I highly recommend Damian Depasquale Creative Studio to anyone looking for high-quality design work, professionalism, and excellent service. Thank you for your dedication and great work!👏",
    author: 'Owen Bugeja',
    role: 'Cashier',
    company: 'Għaqda Armar Triq San Ġwann Nadur',
    category: 'Branding',
  },
];

const TestimonialCard = ({ testimonial, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      data-testid={`testimonial-card-${testimonial.id}`}
      className="group relative bg-card border border-border/50 rounded-lg p-8 card-hover"
    >
      {/* Quote Icon */}
      <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity">
        <Quote className="w-12 h-12 text-primary" />
      </div>

      {/* Category Badge */}
      <span className="inline-block text-xs text-primary font-medium tracking-wider uppercase mb-4 px-3 py-1 bg-primary/10 rounded-full">
        {testimonial.category}
      </span>

      {/* Quote */}
      <blockquote className="text-foreground leading-relaxed mb-6 relative z-10">
        "{testimonial.quote}"
      </blockquote>

      {/* Author */}
      <div className="flex items-center gap-4">
        {/* Avatar placeholder with initials */}
        <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
          <span className="font-['Sora'] font-semibold text-foreground text-sm">
            {testimonial.author.split(' ').map(n => n[0]).join('')}
          </span>
        </div>
        
        <div>
          <p className="font-['Sora'] font-semibold text-foreground">
            {testimonial.author}
          </p>
          <p className="text-sm text-muted-foreground">
            {testimonial.role}, {testimonial.company}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-32 px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Testimonials
          </span>
          <h2
            data-testid="testimonials-title"
            className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-2 tracking-tight"
          >
            Client Feedback
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Don't just take my word for it. Here's what clients have to say about working together.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
